import React from 'react';
import { FaPlus, FaTimes, FaInfoCircle } from 'react-icons/fa';
import { input, select } from '../../utils/designTokens';
import CapabilitiesEditor from './CapabilitiesEditor';

/**
 * Step 3: Capabilities & Dependencies
 * - MCP version, programming language, license
 * - Capabilities list
 * - Dependencies as key-value pairs
 */
const MCPServerFormStep3 = ({ formData, updateFormData }) => {
  const handleChange = (e) => {
    const { name, value } = e.target;
    updateFormData({ [name]: value });
  };

  const handleCapabilitiesChange = (capabilities) => {
    updateFormData({ capabilities });
  };

  const addDependency = () => {
    const key = prompt('Enter dependency name:');
    if (key && key.trim()) {
      const version = prompt('Enter version (or leave empty):') || '*';
      updateFormData({
        dependencies: {
          ...formData.dependencies,
          [key.trim()]: version
        }
      });
    }
  };

  const removeDependency = (key) => {
    const newDeps = { ...formData.dependencies };
    delete newDeps[key];
    updateFormData({ dependencies: newDeps });
  };

  const updateDependencyVersion = (key, newVersion) => {
    updateFormData({
      dependencies: {
        ...formData.dependencies,
        [key]: newVersion
      }
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-spooky-ghost-white">
          Capabilities & Dependencies
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Specify what your server can do and what it requires to run.
        </p>
      </div>

      {/* MCP Version */}
      <div>
        <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300" htmlFor="mcpVersion">
          MCP Version <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="mcpVersion"
          name="mcpVersion"
          value={formData.mcpVersion}
          onChange={handleChange}
          placeholder="e.g., 1.0.0"
          className={input}
          required
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          The Model Context Protocol version your server implements
        </p>
      </div>

      {/* Programming Language */}
      <div>
        <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300" htmlFor="programmingLanguage">
          Programming Language
        </label>
        <select
          id="programmingLanguage"
          name="programmingLanguage"
          value={formData.programmingLanguage}
          onChange={handleChange}
          className={select}
        >
          <option value="">Select language</option>
          <option value="javascript">JavaScript/Node.js</option>
          <option value="typescript">TypeScript</option>
          <option value="python">Python</option>
          <option value="rust">Rust</option>
          <option value="go">Go</option>
          <option value="java">Java</option>
          <option value="csharp">C#</option>
          <option value="ruby">Ruby</option>
          <option value="php">PHP</option>
          <option value="other">Other</option>
        </select>
      </div>

      {/* License */}
      <div>
        <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300" htmlFor="license">
          License
        </label>
        <select
          id="license"
          name="license"
          value={formData.license}
          onChange={handleChange}
          className={select}
        >
          <option value="">Select license</option>
          <option value="MIT">MIT</option>
          <option value="Apache-2.0">Apache 2.0</option>
          <option value="GPL-3.0">GPL 3.0</option>
          <option value="BSD-3-Clause">BSD 3-Clause</option>
          <option value="ISC">ISC</option>
          <option value="Unlicense">Unlicense</option>
          <option value="Proprietary">Proprietary</option>
          <option value="Other">Other</option>
        </select>
      </div>

      {/* Capabilities */}
      <div>
        <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
          Capabilities
        </label>
        <CapabilitiesEditor
          capabilities={formData.capabilities}
          onChange={handleCapabilitiesChange}
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          <FaInfoCircle className="inline mr-1" />
          List the features and operations your server supports
        </p>
      </div>

      {/* Dependencies */}
      <div>
        <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
          Dependencies
        </label>
        <div className="space-y-2">
          {Object.keys(formData.dependencies).length > 0 ? (
            Object.entries(formData.dependencies).map(([key, version]) => (
              <div key={key} className="flex items-center gap-2">
                <input
                  type="text"
                  value={key}
                  disabled
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-spooky-bg-light text-gray-700 dark:text-gray-300"
                />
                <input
                  type="text"
                  value={version}
                  onChange={(e) => updateDependencyVersion(key, e.target.value)}
                  placeholder="Version"
                  className="w-32 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:bg-spooky-bg-medium dark:text-spooky-ghost-white"
                />
                <button
                  type="button"
                  onClick={() => removeDependency(key)}
                  className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition"
                >
                  <FaTimes />
                </button>
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 italic">
              No dependencies added yet
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={addDependency}
          className="mt-3 px-4 py-2 border-2 border-dashed border-cyan-300 dark:border-cyan-700 text-cyan-600 dark:text-cyan-400 rounded-lg hover:bg-cyan-50 dark:hover:bg-cyan-900/20 transition inline-flex items-center gap-2"
        >
          <FaPlus />
          Add Dependency
        </button>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          <FaInfoCircle className="inline mr-1" />
          Runtime dependencies required to run your server
        </p>
      </div>
    </div>
  );
};

export default MCPServerFormStep3;
